import { useEffect, useState } from "react";

// Live LeetCode data via the public alfa-leetcode-api proxy (LeetCode's own
// GraphQL endpoint is CORS-blocked from the browser). Responses are cached in
// localStorage with a TTL and reused as a "last-good" fallback if the proxy is
// cold-starting or unreachable.
const BASE = "https://alfa-leetcode-api.onrender.com";
const TTL = 1000 * 60 * 60 * 6; // 6 hours
const TIMEOUT_MS = 12000; // fail fast to the fallback on cold-starts/hangs
const CACHE_PREFIX = "lc-cache:";

const ENDPOINTS = {
  calendar: (u) => `/${u}/calendar`,
  badges: (u) => `/${u}/badges`,
  solved: (u) => `/${u}/solved`,
};

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? JSON.parse(raw) : null; // { at, data }
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  try {
    localStorage.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ at: Date.now(), data })
    );
  } catch {
    /* ignore quota / private-mode errors */
  }
};

const fetchJson = async (path) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`LeetCode API responded ${res.status}`);
    const data = await res.json();
    if (data && data.errors) throw new Error("LeetCode API returned an error");
    return data;
  } finally {
    clearTimeout(timer);
  }
};

// Returns { data, fromCache, stale }. Falls back to stale cache on failure.
export const getCached = async (key, path, { force = false } = {}) => {
  const cached = readCache(key);
  const fresh = cached && Date.now() - cached.at < TTL;
  if (cached && fresh && !force) {
    return { data: cached.data, fromCache: true, stale: false };
  }
  try {
    const data = await fetchJson(path);
    writeCache(key, data);
    return { data, fromCache: false, stale: false };
  } catch (err) {
    if (cached) return { data: cached.data, fromCache: true, stale: true };
    throw err;
  }
};

// React hook. Seeds initial data synchronously from cache (instant on repeat
// visits) and refreshes in the background.
export const useLeetCode = (kind, username) => {
  const key = `${kind}:${username}`;
  const [state, setState] = useState(() => {
    const cached = username ? readCache(key) : null;
    return {
      data: cached ? cached.data : null,
      loading: true,
      error: null,
      stale: false,
    };
  });

  useEffect(() => {
    let alive = true;
    if (!username || !ENDPOINTS[kind]) {
      setState((s) => ({ ...s, loading: false }));
      return undefined;
    }
    getCached(key, ENDPOINTS[kind](username))
      .then((res) => {
        if (alive)
          setState({
            data: res.data,
            loading: false,
            error: null,
            stale: res.stale,
          });
      })
      .catch((err) => {
        if (alive)
          setState((s) => ({
            data: s.data,
            loading: false,
            error: err.message || "Failed to load",
            stale: false,
          }));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, username]);

  return state;
};

// ---- parsing helpers -------------------------------------------------------

// submissionCalendar is a JSON string: { "<unixSeconds>": count }.
// Returns a map of { "YYYY-MM-DD": count }.
export const parseSubmissionCalendar = (calendarData) => {
  const map = {};
  if (!calendarData) return map;
  const raw = calendarData.submissionCalendar;
  if (!raw) return map;
  let obj;
  try {
    obj = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return map;
  }
  Object.entries(obj).forEach(([ts, count]) => {
    const d = new Date(Number(ts) * 1000);
    if (Number.isNaN(d.getTime())) return;
    const key = d.toISOString().slice(0, 10);
    map[key] = (map[key] || 0) + Number(count);
  });
  return map;
};

export const totalSubmissions = (calendarMap) =>
  Object.values(calendarMap).reduce((sum, n) => sum + n, 0);

// Badge icons can be absolute (assets.leetcode.com) or relative (/static/...).
export const normalizeBadgeIcon = (icon) => {
  if (!icon) return "";
  if (/^https?:\/\//.test(icon)) return icon;
  return `https://leetcode.com${icon.startsWith("/") ? "" : "/"}${icon}`;
};

export default useLeetCode;
